import { useTheme } from "@/contexts/ThemeContext";

/**
 * <Logo /> — the African Polistas mark.
 *
 * Reads the current theme and swaps between the two prepared assets:
 *   • dark mode  → logo-white.png  (white lion + wordmark on transparent)
 *   • light mode → logo-dark.png   (original black lion + wordmark)
 *
 * The logo is ALWAYS rendered on a transparent background per the brand
 * rules — never inside a white rounded tile. Consumers pass a `size` in
 * pixels and, optionally, a `variant` override for edge cases (e.g. the
 * PDF header always needs the dark variant regardless of the viewer's
 * theme, but that's used server-side, not here).
 *
 * NOTE: assets live in /public/brand/ so they're served at the root
 * (avoids Webpack import bloat and lets the PDF generator reference
 * the same file paths).
 */
export function Logo({
  size = 44,
  variant,        // "white" | "dark". leave undefined to auto-swap
  className = "",
  alt = "African Polistas",
  "data-testid": testId = "ap-logo",
}) {
  const { effective } = useTheme();
  const resolvedVariant = variant || (effective === "dark" ? "white" : "dark");
  const src = resolvedVariant === "white"
    ? "/brand/logo-white.png"
    : "/brand/logo-dark.png";

  return (
    <img
      src={src}
      alt={alt}
      width={size}
      height={size}
      className={className}
      style={{
        width: size,
        height: size,
        objectFit: "contain",
        // No background, no rounded tile — logo sits directly on the
        // surface per §2 of the design spec.
        background: "transparent",
      }}
      data-testid={testId}
      draggable={false}
    />
  );
}

/**
 * <LogoLockup /> — the sidebar/topbar composition: logo + wordmark type.
 *
 * Layout per §2:
 *   [Logo 44px] "African Polistas" (Cormorant Garamond 19px)
 *                "OPERATIONS"      (10px letterspaced brass)
 */
export function LogoLockup({ size = 44, className = "", collapsed = false }) {
  return (
    <div
      className={`flex items-center gap-3 ${className}`}
      data-testid="ap-logo-lockup"
    >
      <Logo size={size} />
      {!collapsed && (
        <div className="flex flex-col leading-none">
          <span
            className="font-serif"
            style={{
              fontFamily: "'Cormorant Garamond', ui-serif, Georgia, serif",
              fontSize: "1.1875rem",   // 19px
              fontWeight: 600,
              color: "var(--text-primary)",
              lineHeight: 1.05,
            }}
          >
            African Polistas
          </span>
          <span
            style={{
              fontFamily: "'Instrument Sans', ui-sans-serif, sans-serif",
              fontSize: "0.625rem",    // 10px
              fontWeight: 600,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: "var(--accent-brass)",
              marginTop: "0.25rem",
            }}
          >
            Operations
          </span>
        </div>
      )}
    </div>
  );
}
